import { gql } from '@apollo/client';
import { Box, Stack, Text } from '@chakra-ui/react';
import { convertFromRaw } from 'draft-js';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { DialInInfo } from '@/components/DialInInfo';
import {
  ExternalPlatformIndicator,
  LiveStreamOnTradewingIndicator,
  OnlineConferenceIndicator,
  PreRecordedVideoIndicator,
  AgoraMeetingOnTradewingIndicator
} from '@/components/EventVenueIndicator';
import { VenueFragment } from '@/fragments/venue-fragment';
import { ERegistrationType, EventVenueType } from '@/graphql-types/globalTypes';
import { usePermissions } from '@/hooks/usePermissions';
import { useReactiveEventTiming } from '@/hooks/useReactiveEventTiming';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { InPerson } from '@/modules/events/components/VideoSettings/InPerson';

import { LocationCard_EventFragment } from './graphql-types/LocationCard_EventFragment';

import { Redraft } from '../Redraft';

const TITLE = {
  [EventVenueType.IN_PERSON]: 'Location',
  [EventVenueType.ONLINE]: 'Virtual Access',
  [EventVenueType.HYBRID]: 'Location',
};

export const LocationCard: React.FC<{
  event: LocationCard_EventFragment;
}> & { eventFragment: DocumentNode } = ({ event }) => {
  const { canDoAdminStuffAndAuthenticated } = usePermissions();
  const startDate = event.startDate ? new Date(event.startDate) : new Date();
  const endDate = event.endDate ? new Date(event.endDate) : startDate;
  const { isEventLive } = useReactiveEventTiming({
    startDate,
    endDate,
  });

  return (
    <Card p="6">
      <Stack spacing="4">
        <Text textStyle="xlBodyStrong">
          {event.__typename === 'SimpleEventV2'
            ? TITLE[event.venue.type]
            : TITLE[event.venueType]}
        </Text>

        <Stack spacing="4">
          {event.__typename === 'SimpleEventV2' &&
            (event.venue.type === EventVenueType.IN_PERSON ||
              event.venue.type === EventVenueType.HYBRID) && (
              <InPerson
                address={event.venue.inPersonLocation?.address ?? null}
                description={event.venue.inPersonLocation?.description ?? null}
              />
            )}

          {event.__typename === 'ConferenceEventV2' &&
            (event.venueType === EventVenueType.IN_PERSON ||
              event.venueType === EventVenueType.HYBRID) && (
              <InPerson
                address={event.inPersonLocation?.address ?? null}
                description={event.inPersonLocation?.description ?? null}
              />
            )}

          {event.__typename === 'SimpleEventV2' &&
            event.venue.online &&
            event.venue.online &&
            event.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
            event.venue.online.stream.__typename ===
              'EventVenue_TradewingPlatform_Stream_Live' && (
              <LiveStreamOnTradewingIndicator showAdminInfo={false} />
            )}

          {event.__typename === 'SimpleEventV2' &&
            event.venue.online &&
            event.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
            event.venue.online.stream.__typename ===
              'EventVenue_TradewingPlatform_Stream_Agora_Video_Call' && (
              canDoAdminStuffAndAuthenticated ? (
                <AgoraMeetingOnTradewingIndicator
                  showAdminInfo={true}
                  waitingRoomEnabled={event.venue.online.stream.agoraMeetSettings.waitingRoomEnabled}
                />
              ) : (
                <AgoraMeetingOnTradewingIndicator showAdminInfo={false} />
              )
            )}


          {event.__typename === 'SimpleEventV2' &&
            event.venue.online &&
            event.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
            event.venue.online.stream.__typename ===
              'EventVenue_TradewingPlatform_Stream_Prerecorded' && (
              <PreRecordedVideoIndicator showAdminInfo={false} />
            )}

          {event.__typename === 'SimpleEventV2' &&
            event.venue.online &&
            event.venue.online &&
            event.venue.online.__typename === 'EventVenue_ExternalPlatform' && (
              <ExternalPlatformIndicator
                externalPlatformName={event.venue.online.externalPlatformName}
              />
            )}

          {event.__typename === 'ConferenceEventV2' &&
            (event.venueType === EventVenueType.ONLINE ||
              event.venueType === EventVenueType.HYBRID) && (
              <OnlineConferenceIndicator showAdminInfo={false} />
            )}

          {event.__typename === 'SimpleEventV2' &&
            (event.canCurrentUserJoinEventEarly ||
              (isEventLive &&
                (event.registrationType === ERegistrationType.OPEN ||
                  !!event.currentValidUserRegistration))) &&
            event.venue.online && (
              <>
                {event.venue.online.__typename ===
                  'EventVenue_TradewingPlatform' &&
                  event.venue.online.stream.__typename ===
                    'EventVenue_TradewingPlatform_Stream_Live' &&
                  event.venue.online.stream.virtualEvent &&
                  event.venue.online.stream.virtualEvent.dialInEnabled &&
                  event.venue.online.stream.virtualEvent.dialInPhoneNumber && (
                    <Box bg="gray.50" p="4" borderRadius="xl">
                      <DialInInfo
                        eventName={event.name}
                        dialInPhoneNumber={
                          event.venue.online.stream.virtualEvent
                            .dialInPhoneNumber
                        }
                        dialInCode={
                          event.venue.online.stream.virtualEvent.dialInCode
                        }
                        listenerDialInCode={
                          event.venue.online.stream.virtualEvent
                            .listenerDialInCode
                        }
                        isCurrentUserSpeaker={event.isCurrentUserSpeaker}
                        isParticipationAllowed={
                          event.venue.online.stream.virtualEvent
                            .maxParticipants > 0
                        }
                      />
                    </Box>
                  )}
                {event.venue.online.__typename ===
                  'EventVenue_ExternalPlatform' &&
                  event.venue.online.additionalDetails &&
                  convertFromRaw(
                    event.venue.online.additionalDetails
                  ).getPlainText().length > 0 && (
                    <Box bg="gray.50" p="4" borderRadius="xl">
                      <Prose>
                        <Redraft
                          rawDraftContentState={
                            event.venue.online.additionalDetails
                          }
                        />
                      </Prose>
                    </Box>
                  )}
              </>
            )}
        </Stack>
      </Stack>
    </Card>
  );
};

LocationCard.eventFragment = gql`
  fragment LocationCard_EventFragment on EventV2 {
    _id
    name
    startDate
    endDate
    isCurrentUserSpeaker
    registrationType
    currentValidUserRegistration {
      _id
    }
    ... on SimpleEventV2 {
      canCurrentUserJoinEventEarly
      venue {
        ...VenueFragment
      }
    }
    ... on ConferenceEventV2 {
      venueType
      inPersonLocation {
        address {
          administrativeArea
          country
          locality
          name
          postalCode
          streetAddress
          streetAddressLine2
        }
        description
      }
    }
  }
  ${VenueFragment}
`;
